import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BASE_API, DecryptToken, enable_stream } from "../helper/Constants";
import { useDispatch, useSelector } from "react-redux";
import { fetchBalance, updateLiveCallsCount } from "../slices/WalletSlice";
import { DEFAULT_LIVE_CALL_COUNTS, normalizeLiveCallCounts } from "../helper/utils";
import PrintBalance from "../components/wallet-balance/PrintBalance";
import {
  Bell, User, CreditCard, LogOut,
  ChevronDown, Menu, Sun, Moon, Phone, AlertTriangle
} from "lucide-react";

// ─── Dark Mode Hook ───────────────────────────────────────────────────────────
const useDarkMode = () => {
  const [dark, setDark] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("theme") === "dark" ||
      (!localStorage.getItem("theme") && window.matchMedia("(prefers-color-scheme: dark)").matches);
  });

  useEffect(() => {
    const root = document.documentElement;
    if (dark) {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [dark]);

  return [dark, () => setDark((p) => !p)];
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDateTime = (dateTime) => {
  if (!dateTime) return "";
  const d = new Date(dateTime);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const truncate = (str, max = 10) =>
  str && str.length > max ? `${str.substring(0, max)}…` : str;

// ─── Dropdown Wrapper ─────────────────────────────────────────────────────────
const Dropdown = ({ trigger, children, align = "right" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <div onClick={() => setOpen((p) => !p)} className="cursor-pointer">
        {trigger}
      </div>
      {open && (
        <div
          className={`absolute ${align === "right" ? "right-0" : "left-0"} top-full mt-2 z-50 
            min-w-[220px] rounded-xl bg-white dark:bg-slate-800 shadow-xl 
            ring-1 ring-black/5 dark:ring-white/10 overflow-hidden
            animate-in fade-in slide-in-from-top-2 duration-150`}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      )}
    </div>
  );
};

// ─── Live Call Pill ───────────────────────────────────────────────────────────
const CallPill = ({ count, label, color }) => (
  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${color}`}>
    <Phone size={11} />
    <span>{count} {label}</span>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Header = ({ navHandler, user_id }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [dark, toggleDark] = useDarkMode();

  const [userEmail, setUserEmail] = useState(null);
  const [userType, setUserType] = useState(null);
  const [showButton, setShowButton] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifError, setNotifError] = useState(null);
  const [showExpirationWarning, setShowExpirationWarning] = useState(false);

  const endDate = useSelector((state) => state.wallet.endDate);
  const liveCallCounts = useSelector(
    (state) => state.wallet.liveCallsCount || DEFAULT_LIVE_CALL_COUNTS
  );

  // ── Bootstrap: decode token once
  useEffect(() => {
    try {
      const token = localStorage.getItem("psx_token");
      const decoded = DecryptToken(token);
      if (decoded) {
        setUserEmail(decoded.first_name);
        setUserType(decoded.user_type);
      }
    } catch (e) {
      console.error("Token error:", e);
    }
  }, []);

  // ── Balance
  useEffect(() => { dispatch(fetchBalance()); }, [dispatch]);

  // ── Expiration warning
  useEffect(() => {
    if (!endDate) return;
    const check = () => {
      const days = Math.ceil((new Date(endDate) - new Date()) / 86400000);
      setShowExpirationWarning(days <= 10 && days > 0);
    };
    check();
    const id = setInterval(check, 86400000);
    return () => clearInterval(id);
  }, [endDate]);

  // ── Responsive show button
  useEffect(() => {
    const handler = () => setShowButton(window.innerWidth > 1200);
    window.addEventListener("resize", handler);
    handler();
    return () => window.removeEventListener("resize", handler);
  }, []);

  // ── Notifications SSE
  useEffect(() => {
    if (!enable_stream) return;
    const es = new EventSource(`${BASE_API}api/notifications/stream`);
    es.onmessage = ({ data }) => {
      try {
        const { type, data: payload } = JSON.parse(data);
        if (type === "init") {
          setNotifications(payload);
          setUnreadCount(payload.filter((n) => !n.isRead).length);
        } else if (type === "create") {
          setNotifications((p) => [payload, ...p]);
          if (!payload.isRead) setUnreadCount((c) => c + 1);
        } else if (type === "delete") {
          setNotifications((p) => {
            const next = p.filter((n) => String(n.id) !== String(payload.id));
            setUnreadCount(next.filter((n) => !n.isRead).length);
            return next;
          });
        }
      } catch (e) {
        console.error("SSE parse error:", e);
        setNotifError("Failed to process notification.");
      }
    };
    es.onerror = () => {
      setNotifError("Connection lost.");
      es.close();
    };
    return () => es.close();
  }, []);

  // ── Live calls SSE
  const handleLiveSSE = useCallback(({ data }) => {
    try {
      const parsed = JSON.parse(data);
      if (parsed.type === "update" && parsed.data?.counts) {
        dispatch(updateLiveCallsCount(parsed.data.counts));
      }
    } catch (e) {
      console.error("Live call SSE error:", e);
    }
  }, [dispatch]);

  const callCounts = normalizeLiveCallCounts(liveCallCounts);
  const showCampaignCallPill = userType !== 3 || callCounts.campaignCallsCount > 0;
  const showExtensionCallPill = userType !== 4 || callCounts.extensionCallsCount > 0;

  useEffect(() => {
    if (!user_id || !enable_stream) return;
    const es = new EventSource(`${BASE_API}api/live-calls/stream?user_id=${user_id}`);
    es.onmessage = handleLiveSSE;
    es.onerror = () => es.close();
    return () => es.close();
  }, [user_id, handleLiveSSE]);

  // ── Handlers
  const handleLogout = () => {
    localStorage.removeItem("psx_token");
    navigate("/", { replace: true });
    toast.success("Logged out successfully");
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-[65px] z-50 
      bg-white/90 dark:bg-slate-900/90 backdrop-blur-md 
      border-b border-slate-100 dark:border-slate-800 
      flex items-center px-4 md:px-6 transition-colors duration-300">

      <div className="flex items-center justify-between w-full gap-4">

        {/* ── Left: Logo + Mobile Menu + Call Pills ── */}
        <div className="flex items-center gap-4">
          {/* Logo */}
          <Link to="/dashboard" className="hidden lg:flex items-center gap-2.5 no-underline group">
            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md shadow-blue-500/25 group-hover:scale-105 transition-transform duration-200">
            </div>
            <span className="text-lg font-extrabold tracking-tight text-slate-800 dark:text-white">
              CALL<span className="text-blue-500">Adda</span>
            </span>
          </Link>

          {/* Mobile menu icon */}
          <button
            onClick={navHandler}
            className="lg:hidden p-1.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <Menu size={22} />
          </button>

          {/* Live call pills */}
          <div className="hidden md:flex items-center gap-2">
            {showCampaignCallPill && (
              <CallPill
                count={callCounts.campaignCallsCount}
                label="Campaign"
                color="bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
              />
            )}
            {showExtensionCallPill && (
              <CallPill
                count={callCounts.extensionCallsCount}
                label="Extension"
                color="bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-400"
              />
            )}
          </div>
        </div>

        {/* ── Right: Nav Items ── */}
        <div className="flex items-center gap-1 md:gap-2">

          {/* Expiration warning */}
          {showExpirationWarning && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 text-xs font-semibold">
              <AlertTriangle size={13} />
              <span className="hidden sm:block">Plan Expiring Soon</span>
            </div>
          )}

          {/* Expire date */}
          {showButton && endDate && (
            <span className="hidden xl:block text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-lg font-medium">
              Expires: {formatDateTime(endDate)}
            </span>
          )}

          {/* Wallet balance */}
          <div className="text-slate-700 dark:text-slate-200">
            <PrintBalance userId={user_id} />
          </div>

          {/* Dark mode toggle */}
          <button
            onClick={toggleDark}
            className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-all duration-200"
            aria-label="Toggle dark mode"
          >
            {dark ? <Sun size={17} /> : <Moon size={17} />}
          </button>

          {/* Notifications */}
          <Dropdown
            trigger={
              <div className="relative p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-all duration-200">
                <Bell size={17} />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[9px] font-bold text-white">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </div>
            }
          >
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">Notifications</p>
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mt-0.5">
                {notifications.length > 0
                  ? `${notifications.length} notification${notifications.length !== 1 ? "s" : ""}`
                  : "You're all caught up"}
              </p>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifError && (
                <div className="px-4 py-3 text-xs text-red-500">{notifError}</div>
              )}
              {!notifError && notifications.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-slate-400 dark:text-slate-500">
                  No notifications yet
                </div>
              )}
              {notifications.map((n) => (
                <div
                  key={String(n.id)}
                  className={`px-4 py-3 border-b border-slate-50 dark:border-slate-700/50 last:border-0 
                    hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${!n.isRead ? "bg-blue-50/50 dark:bg-blue-500/5" : ""}`}
                >
                  <div className="flex gap-2.5">
                    <div className="mt-0.5 p-1.5 rounded-full bg-blue-100 dark:bg-blue-500/10 flex-shrink-0">
                      <Bell size={10} className="text-blue-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200 leading-snug">{n.message}</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{formatDateTime(n.createdAt)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Dropdown>

          {/* Profile */}
          <Dropdown
            trigger={
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer">
                <div className="h-7 w-7 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                  {userEmail ? userEmail[0].toUpperCase() : "U"}
                </div>
                {userEmail && (
                  <span className="hidden md:block text-sm font-medium text-slate-700 dark:text-slate-200">
                    {truncate(userEmail)}
                  </span>
                )}
                <ChevronDown size={13} className="text-slate-400 dark:text-slate-500" />
              </div>
            }
          >
            {/* Profile header */}
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white font-bold">
                  {userEmail ? userEmail[0].toUpperCase() : "U"}
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800 dark:text-white">{userEmail}</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500">Web Designer</p>
                </div>
              </div>
            </div>

            {/* Profile links */}
            {[
              { to: "/profile", icon: User, label: "My Profile" },
              { to: "/purchaseplan", icon: CreditCard, label: "Purchase Plan" },
            ].map(({ to, icon: Icon, label }) => (
              <Link
                key={to}
                to={to}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-300 
                  hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-white 
                  transition-colors no-underline"
              >
                <Icon size={15} className="text-slate-400 dark:text-slate-500" />
                <span>{label}</span>
              </Link>
            ))}

            <div className="my-1 border-t border-slate-100 dark:border-slate-700" />

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 dark:text-red-400 
                hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
            >
              <LogOut size={15} />
              <span>Sign Out</span>
            </button>
          </Dropdown>
        </div>
      </div>
    </header>
  );
};

export default Header;
